/**
 * Who may turn on a tenant's scan schedule, and whether it is on: the inputs
 * of the never-scanned brief (`firstAssessmentAction`, brief-helpers.ts),
 * loaded by `useTenantScanSchedule`.
 */
import type { CurrentUserDto } from '@/lib/api/admin'
import type { ScanScheduleState } from './brief-helpers'

/**
 * Whether the viewer may edit a tenant's scan schedule: GlobalAdmin or
 * SecurityAdmin, the Container's gate on POST /tenants/{id}/scan-settings
 * (TenantsController.UpdateTenantScanSettings). Fails closed: while `/me`
 * loads or after it fails, the viewer gets the member copy.
 */
export function canEditScanSchedule(user: Pick<CurrentUserDto, 'role'> | null | undefined): boolean {
  return user?.role === 'GlobalAdmin' || user?.role === 'SecurityAdmin'
}

/**
 * 'on' when the schedule is enabled and not Manual, the tenants
 * TenantSchedulingService scans; 'off' otherwise; 'pending' without a full
 * answer (loading, failed, or not asked).
 */
export function scanScheduleState(
  settings: { scanScheduleEnabled?: unknown; scanFrequency?: unknown } | null | undefined,
): ScanScheduleState {
  if (!settings || typeof settings.scanScheduleEnabled !== 'boolean' || typeof settings.scanFrequency !== 'string') {
    return 'pending'
  }
  return settings.scanScheduleEnabled && settings.scanFrequency !== 'Manual' ? 'on' : 'off'
}

/**
 * When the never-scanned brief asks for the tenant's scan schedule: a tenant
 * is selected, it has never been scanned, and `/me` has answered without
 * naming a Platform Admin (whose brief runs the first assessment instead, so
 * a Platform Admin never fires the query). A failed `/me` counts as answered:
 * the role then fails closed, but the schedule can still be told.
 */
export function scanScheduleQueryEnabled(opts: {
  tenantId: string | undefined
  neverScanned: boolean
  currentUser: Pick<CurrentUserDto, 'isPlatformAdmin'> | null | undefined
  currentUserLoading: boolean
}): boolean {
  return !!opts.tenantId && opts.neverScanned && !opts.currentUserLoading && opts.currentUser?.isPlatformAdmin !== true
}
